import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Dialog, Drawer } from '@material-ui/core'
import Button, { IconButton } from 'components/ds/Button'
import Badge from 'components/ds/Badge'
import Icon from 'components/ds/Icon'
import ReactSelect from 'react-select'
import { Input } from 'components/ds/Input'
import { useFieldId } from 'components/ds/Field'
import fieldStyles from 'components/ds/Field/Field.module.scss'
import { Checkbox, Radio, Switch, ChoiceGroup } from 'components/ds/Choice'
import { statesOptions } from 'constants/constantVariables'
import { Modal, Tooltip } from 'components/ds/Feedback'
import CreditCost from 'components/shared/CreditCost'
import { Toast } from 'components/shared/Toaster'
import AuthService from 'tools/AuthService'
import { useAppContext } from 'context/AppContext'
import {
    healthcareSearch,
    healthcareSearchDemoMode,
    getHealthcareSearchHistory,
    getHealthcareSearchResult,
    saveHealthcareSearchRecord,
    getHealthcareSearchSavedRecords,
    deleteHealthcareSearchSavedRecord,
    getHealthcareSearchTargets,
    addHealthcareSearchTarget,
    removeHealthcareSearchTarget,
} from 'actions/healthcareSearch'
import HealthcareCheckDetail, { deriveVerdict } from './SavedRecord/HealthcareCheckDetail'
import MatchTabs, { defaultList } from './MatchTabs'
import { LISTS, EXCLUSION_LISTS, screen, decisionFor, snapshotOf, stateName } from './screening'
import styles from './EntityBoard.module.scss'

/**
 * Application Healthcare Search tab: one row per person or business (the business, each owner, and
 * anyone added for search), each with its own search, history, saved records and status. Searches
 * carry a targetKey (BUSINESS, OWNER:<uuid>, ADDED_PERSON:<id>, ADDED_BUSINESS:<id>) so the backend
 * history can be grouped back per person.
 */

const STALE_DAYS = 30

// One color rule: red = on a list (saved record, or an exact match to check), amber = something to
// check, green = clear, grey = information. Most serious first.
const STATUS = {
    SANCTIONED: { label: 'Sanctioned', tone: 'declined', rank: 0,
        hint: 'A record of an OFAC sanctions match was saved.' },
    EXCLUDED: { label: 'Excluded', tone: 'declined', rank: 1,
        hint: 'A record of a match on LEIE, SAM.gov or a State Medicaid list was saved.' },
    PRIOR: { label: 'Prior sanctions', tone: 'watch', rank: 1.5,
        hint: 'A record was saved for a past exclusion that has since been lifted (reinstated).' },
    // Both read "Needs review"; red when an exact match is waiting, amber for similar names only.
    EXACT: { label: 'Needs review', tone: 'declined', outline: true, rank: 2,
        hint: 'A list has the same name or NPI. Open it to save the record, or mark them as clear.' },
    TO_REVIEW: { label: 'Needs review', tone: 'watch', rank: 3,
        hint: 'Lists have similar names. Open it to check them, or mark them as clear.' },
    ERROR: { label: 'Error', tone: 'watch', rank: 4,
        hint: "A list didn't respond, so they're not fully checked. Search again." },
    LOADING: { label: 'Loading', tone: 'closed', rank: 5, hint: 'Loading the latest result.' },
    NOT_SEARCHED: { label: 'Not searched', tone: 'closed', rank: 5, hint: 'No search has been run yet.' },
    SEARCHING: { label: 'Searching', tone: 'closed', rank: 5, hint: 'Checking all five lists now.' },
    QUEUED: { label: 'Queued', tone: 'closed', rank: 5, hint: 'Waiting for its turn in Search all.' },
    CLEAR: { label: 'Clear', tone: 'approved', rank: 6,
        hint: 'Nothing on any sanctions or exclusion list, or someone checked the similar names and marked them clear.' },
}

const cx = (...p) => p.filter(Boolean).join(' ')

const newId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 8)

const addedKey = (draft) => `${draft.kind === 'business' ? 'ADDED_BUSINESS' : 'ADDED_PERSON'}:${draft.id}`

// Owner DOBs arrive as YYYY-MM-DD (sometimes with a time) or MM/DD/YYYY; the date input wants YYYY-MM-DD.
const normDob = (d) => {
    if (!d) return ''
    if (/^\d{4}-\d{2}-\d{2}/.test(d)) return d.slice(0, 10)
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(d)
    return m ? `${m[3]}-${m[1]}-${m[2]}` : ''
}

// Stored requests carry LEIE-shaped YYYYMMDD.
const requestDobToInput = (d) => (d && /^\d{8}$/.test(d) ? `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}` : '')

const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '')
const fmtDateTime = (iso) => (iso
    ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '')

const isStale = (entry) => !!entry?.searchedAt
    && Date.now() - new Date(entry.searchedAt).getTime() > STALE_DAYS * 24 * 3600 * 1000

// Saved records are matched to cards by key and risk status (see screening.js).

const describeOutcome = (entry) => {
    if (!entry.totalCards) return 'No matches'
    const parts = [`${entry.totalCards} ${entry.totalCards === 1 ? 'match' : 'matches'}`]
    if (entry.excludedCount) parts.push(`${entry.excludedCount} excluded`)
    if (entry.priorSanctionsCount) parts.push(`${entry.priorSanctionsCount} prior sanctions`)
    return parts.join(' · ')
}

// People are searched as "LAST, FIRST"; shown the way the name reads.
const readableName = (name) => {
    if (!name || !name.includes(',')) return name || ''
    const [last, first] = name.split(',').map(x => x.trim())
    return [first, last].filter(Boolean).join(' ')
}

const inputsFromEntity = (entity, lastRequest) => {
    const base = {
        firstName: entity.firstName || '',
        lastName: entity.lastName || '',
        name: entity.kind === 'business' ? (entity.businessName ?? entity.name ?? '') : '',
        npi: entity.npi || '',
        state: entity.state || '',
        dob: entity.dob || '',
        includeInactive: false,
    }
    if (!lastRequest) return base
    // A provider renamed on the client portal: search the portal's details, keep the last filters.
    if (entity.appChanged) {
        return {
            ...base,
            state: lastRequest.state || '',
            dob: requestDobToInput(lastRequest.dob),
            includeInactive: !!lastRequest.includeInactive,
        }
    }
    // Re-run what was searched last time, including a different name or filters left out on purpose.
    const [lastName = '', firstName = ''] = (lastRequest.name || '').split(',').map(x => x.trim())
    return {
        ...base,
        name: entity.kind === 'business' ? (lastRequest.name || '') : '',
        firstName: entity.kind === 'business' ? '' : firstName,
        lastName: entity.kind === 'business' ? '' : lastName,
        // An NPI on file (added to the owner or business after the last search) wins over none.
        npi: lastRequest.npi || entity.npi || '',
        state: lastRequest.state || '',
        dob: requestDobToInput(lastRequest.dob),
        includeInactive: !!lastRequest.includeInactive,
    }
}

const buildPayload = (entity, inputs) => {
    const first = inputs.firstName.trim()
    const last = inputs.lastName.trim()
    // People always go out with a comma ("LAST, FIRST", "LAST," or ", FIRST") so they're searched as
    // individuals; without one the backend treats the name as a business.
    const name = entity.kind === 'business'
        ? inputs.name.trim()
        : (last || first ? `${last},${first ? ` ${first}` : ''}` : '')
    const payload = { targetKey: entity.key }
    if (name) payload.name = name
    if (inputs.npi.trim()) payload.npi = inputs.npi.trim()
    if (inputs.state) payload.state = inputs.state
    if (entity.kind !== 'business' && inputs.dob) payload.dob = inputs.dob.replace(/-/g, '')
    if (inputs.includeInactive) payload.includeInactive = true
    if (entity.added) {
        payload.targetLabel = entity.kind === 'business'
            ? inputs.name.trim()
            : [inputs.firstName.trim(), inputs.lastName.trim()].filter(Boolean).join(' ')
    }
    return payload
}

// The search inputs carry the name and NPI the client portal lists for this provider.
const sameAsListed = (entity, inputs) => {
    const t = (v) => (v || '').trim()
    const named = entity.kind === 'business'
        ? t(inputs.name) === t(entity.businessName)
        : t(inputs.firstName) === t(entity.firstName) && t(inputs.lastName) === t(entity.lastName)
    return named && t(inputs.npi) === t(entity.npi)
}

const hasSearchInput = (payload) => !!((payload.name && payload.name.replace(',', '').trim()) || payload.npi)

const EntityBoard = ({ applicantId, creditCosts, business, owners }) => {
    const { isDemoMode } = useAppContext()
    const { creditUsageEnabled } = AuthService.getClientFeatures()
    const cost = useMemo(() => {
        const row = (creditCosts || []).find(c => c?.requestType === 'HEALTHCARE_SEARCH')
        return row?.creditCost != null ? row.creditCost : null
    }, [creditCosts])
    const showCost = creditUsageEnabled && cost != null

    const [history, setHistory] = useState([])
    const [savedRecords, setSavedRecords] = useState([])
    const [savedTick, setSavedTick] = useState(0)
    const [running, setRunning] = useState({})          // key -> 'queued' | 'searching'
    const [batch, setBatch] = useState(null)            // { total, done, current }
    const stopRef = useRef(false)
    const [drawerKey, setDrawerKey] = useState(null)
    const [drawerTab, setDrawerTab] = useState(null)    // list tab the drawer opens on (OFAC cell / list box)
    const [draft, setDraft] = useState(null)            // added entity before its first search
    const [confirm, setConfirm] = useState(null)        // { entity, inputs }
    const [removing, setRemoving] = useState(null)      // added entity awaiting "Remove from list" confirmation
    const [showRemoved, setShowRemoved] = useState(false)
    const [showCoverage, setShowCoverage] = useState(false)
    const [recordsKey, setRecordsKey] = useState(null)  // whose saved records window is open
    const [freshResults, setFreshResults] = useState({}) // key -> response of a search run this session
    const [results, setResults] = useState({})           // searchId -> stored response, for row status

    const loadHistory = () => {
        if (!applicantId) return Promise.resolve([])
        return getHealthcareSearchHistory({ applicationId: applicantId, isDemoMode })()
            .then(res => {
                const list = Array.isArray(res) ? res : []
                setHistory(list)
                return list
            })
            .catch(() => [])
    }

    useEffect(() => { loadHistory() }, [applicantId, isDemoMode]) // eslint-disable-line react-hooks/exhaustive-deps

    const [targets, setTargets] = useState([])           // people and businesses added to the list
    const loadTargets = () => {
        if (!applicantId) return Promise.resolve()
        return getHealthcareSearchTargets({ applicationId: applicantId })()
            .then(res => setTargets(Array.isArray(res) ? res : []))
            .catch(() => {})
    }
    useEffect(() => { loadTargets() }, [applicantId]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!applicantId) return
        getHealthcareSearchSavedRecords({ applicationId: applicantId })()
            .then(res => setSavedRecords(Array.isArray(res) ? res : []))
            .catch(() => {})
    }, [applicantId, savedTick])

    const baseEntities = useMemo(() => {
        const out = []
        if (business?.name) {
            out.push({
                key: 'BUSINESS', kind: 'business', type: 'Business', name: business.name,
                state: business.state || '', npi: business.npi || '',
            })
        }
        for (const o of owners || []) {
            if (!o?.uuid || !(o.firstName || o.lastName)) continue
            const state = o.address?.state
            out.push({
                key: `OWNER:${o.uuid}`, kind: 'person', type: 'Owner',
                firstName: o.firstName || '', lastName: o.lastName || '',
                name: [o.firstName, o.lastName].filter(Boolean).join(' '),
                state: state && state.length === 2 ? state : '',
                dob: normDob(o.dateOfBirth),
                npi: o.npi || '',
            })
        }
        return out
    }, [business, owners])

    // Searches from before the per-entity tab have no targetKey; match them by the name the old
    // prefill chips sent (business name, or "Last, First" for owners).
    const keyForEntry = useMemo(() => {
        const byName = new Map()
        for (const e of baseEntities) {
            const n = e.kind === 'business' ? e.name : [e.lastName, e.firstName].filter(Boolean).join(', ')
            if (n) byName.set(n.toUpperCase(), e.key)
        }
        return (entry) => entry.request?.targetKey || byName.get((entry.request?.name || '').toUpperCase()) || null
    }, [baseEntities])

    const historyByKey = useMemo(() => {
        const m = new Map()
        for (const entry of history) {
            const k = keyForEntry(entry)
            if (!k) continue
            if (!m.has(k)) m.set(k, [])
            m.get(k).push(entry)
        }
        return m
    }, [history, keyForEntry])

    // Saved targets first (searched or not), then rows known only from their searches (added before
    // targets were stored). A removed target hides its searches too.
    const addedEntities = useMemo(() => {
        const out = []
        const known = new Set()
        for (const t of targets) {
            known.add(t.targetKey)
            if (t.removed) continue
            const kind = t.kind === 'BUSINESS' ? 'business' : 'person'
            const label = kind === 'business' ? (t.businessName || '') : [t.firstName, t.lastName].filter(Boolean).join(' ')
            // Client-portal providers nobody corrected follow the portal; flag a rename there since the last search.
            const followsPortal = t.source === 'APPLICATION' && !t.stored
            const lastReq = followsPortal ? (historyByKey.get(t.targetKey) || [])[0]?.request : null
            out.push({
                key: t.targetKey, kind, added: true, fromApplication: t.source === 'APPLICATION', followsPortal,
                appChanged: !!lastReq && ((lastReq.targetLabel || '') !== label || (lastReq.npi || '') !== (t.npi || '')),
                type: kind === 'business' ? 'Added business' : 'Added person',
                name: label || (t.npi ? `NPI ${t.npi}` : 'Unnamed'),
                businessName: t.businessName || '', firstName: t.firstName || '', lastName: t.lastName || '', npi: t.npi || '',
                state: t.state || '', dob: normDob(t.dob),
            })
        }
        for (const [k, entries] of historyByKey) {
            if (!k.startsWith('ADDED_') || known.has(k)) continue
            const req = entries[0].request || {}
            const kind = k.startsWith('ADDED_BUSINESS') ? 'business' : 'person'
            const label = req.targetLabel || req.name || 'Added for search'
            const [lastName = '', firstName = ''] = kind === 'person' && req.name ? req.name.split(', ') : []
            out.push({
                key: k, kind, added: true, type: kind === 'business' ? 'Added business' : 'Added person', name: label,
                firstName, lastName, npi: '', state: '', dob: '',
            })
        }
        return out
    }, [targets, historyByKey])

    // Removed added entities, offered back under the table. Rows removed before targets stored
    // details take their name from their latest search.
    const removedEntities = useMemo(() => targets.filter(t => t.removed).map(t => {
        const kind = t.kind === 'BUSINESS' ? 'business' : 'person'
        const entries = historyByKey.get(t.targetKey) || []
        const req = entries[0]?.request || {}
        const name = (kind === 'business' ? t.businessName : [t.firstName, t.lastName].filter(Boolean).join(' '))
            || req.targetLabel || req.name || (t.npi ? `NPI ${t.npi}` : 'Unnamed')
        return { target: t, kind, name, req, searches: entries.length, fromApplication: t.source === 'APPLICATION' }
    }), [targets, historyByKey])

    const entities = useMemo(() => [...baseEntities, ...addedEntities], [baseEntities, addedEntities])

    const draftEntity = draft && {
        key: addedKey(draft), kind: draft.kind, added: true, type: draft.kind === 'business' ? 'Added business' : 'Added person',
        name: '', firstName: '', lastName: '', npi: '', state: '', dob: '', isDraft: true,
    }
    const drawerEntity = entities.find(e => e.key === drawerKey) || (draftEntity?.key === drawerKey ? draftEntity : null)

    const latestOf = (key) => historyByKey.get(key)?.[0] || null
    const resultOf = (key) => results[latestOf(key)?.searchId] || null

    // Row status needs each person's latest cards; reopening a stored result is free.
    useEffect(() => {
        for (const entries of historyByKey.values()) {
            const id = entries[0].searchId
            if (results[id] !== undefined) continue
            setResults(r => ({ ...r, [id]: null }))
            getHealthcareSearchResult({ applicationId: applicantId, searchId: id, isDemoMode })()
                .then(res => setResults(r => ({ ...r, [id]: res })))
                .catch(() => {})
        }
    }, [historyByKey]) // eslint-disable-line react-hooks/exhaustive-deps

    // Saved records for this person; ones saved before targets existed are matched by card key.
    const decisionsFor = (key) => {
        const cardKeys = new Set((resultOf(key)?.cards || []).map(c => c.matchedKey))
        return savedRecords.filter(r => r.targetKey === key || (!r.targetKey && cardKeys.has(r.matchedKey)))
    }
    const screenOf = (key) => screen(latestOf(key), resultOf(key), decisionsFor(key))
    const rowStatus = (key) => {
        if (running[key] === 'searching') return 'SEARCHING'
        if (running[key] === 'queued') return 'QUEUED'
        return screenOf(key).status
    }

    // Most serious first; ties keep application order.
    const liveSorted = useMemo(() => {
        const rank = (e) => STATUS[screenOf(e.key).status].rank
        return entities.map((e, i) => ({ e, i })).sort((a, b) => rank(a.e) - rank(b.e) || a.i - b.i).map(x => x.e)
    }, [entities, historyByKey, results, savedRecords]) // eslint-disable-line react-hooks/exhaustive-deps
    // While the drawer is open the rows keep their places, so the one being worked on doesn't jump
    // around behind it; they re-sort when it closes.
    const frozenOrder = useRef(null)
    if (!drawerKey) frozenOrder.current = null
    else if (!frozenOrder.current) frozenOrder.current = liveSorted.map(e => e.key)
    const sorted = frozenOrder.current
        ? liveSorted.map((e, i) => ({ e, i: frozenOrder.current.includes(e.key) ? frozenOrder.current.indexOf(e.key) : 1e6 + i }))
            .sort((a, b) => a.i - b.i).map(x => x.e)
        : liveSorted

    const notSearched = entities.filter(e => !latestOf(e.key))
    const busy = !!batch || Object.keys(running).length > 0
    // "Bulk search": pick who to search. People never searched start ticked; ones already searched
    // start unticked, so nobody is charged twice by accident.
    const [bulkPicked, setBulkPicked] = useState(null)   // Set of keys while the dialog is open
    const openBulk = () => setBulkPicked(new Set(notSearched.map(e => e.key)))
    const toggleBulk = (key) => setBulkPicked(prev => {
        const next = new Set(prev)
        if (next.has(key)) next.delete(key)
        else next.add(key)
        return next
    })
    const bulkList = bulkPicked ? entities.filter(e => bulkPicked.has(e.key)) : []

    // One search; resolves true on success so a batch knows whether to keep going.
    const runSearch = (entity, inputs) => {
        const payload = buildPayload(entity, inputs)
        if (!hasSearchInput(payload)) {
            Toast({ type: 'error', message: 'Enter a name or NPI to search' })
            return Promise.resolve(false)
        }
        setRunning(r => ({ ...r, [entity.key]: 'searching' }))
        const runner = isDemoMode ? healthcareSearchDemoMode : healthcareSearch
        // Added entities keep what was searched: a new one joins the list, an existing one takes the
        // corrected details (so the row's name follows a fixed typo).
        // A client-portal provider searched as listed isn't stored, so it keeps following the portal.
        const asListed = entity.followsPortal && sameAsListed(entity, inputs)
        const saved = entity.added && !asListed
            ? addHealthcareSearchTarget({ applicationId: applicantId, payload: targetPayload(entity, inputs) })()
                .then(() => loadTargets())
            : Promise.resolve()
        return saved.then(() => runner({ payload, applicationId: applicantId })())
            // History first, so the drawer marks the new search as the one showing.
            .then(res => loadHistory().then(list => {
                const mine = list.find(entry => keyForEntry(entry) === entity.key)
                if (mine) setResults(r => ({ ...r, [mine.searchId]: res }))
                setFreshResults(f => ({ ...f, [entity.key]: { searchId: mine?.searchId || null, response: res } }))
                return true
            }))
            .catch(() => {
                Toast({ type: 'error', message: `Healthcare search failed for ${payload.targetLabel || payload.name || 'this entity'}` })
                return false
            })
            .finally(() => setRunning(r => {
                const next = { ...r }
                delete next[entity.key]
                return next
            }))
    }

    const runBatch = async (list) => {
        if (!list.length || busy) return
        stopRef.current = false
        setRunning(Object.fromEntries(list.map(e => [e.key, 'queued'])))
        setBatch({ total: list.length, done: 0, current: list[0].name })
        for (let i = 0; i < list.length; i++) {
            if (stopRef.current) break
            const e = list[i]
            setBatch(b => ({ ...b, current: e.name }))
            const ok = await runSearch(e, inputsFromEntity(e, latestOf(e.key)?.request))
            setBatch(b => ({ ...b, done: i + 1 }))
            if (!ok) break
        }
        setRunning({})
        setBatch(null)
    }

    const requestRun = (entity, inputs) => {
        if (blockDuplicate(entity, inputs)) return
        if (latestOf(entity.key)) setConfirm({ entity, inputs })
        else runSearch(entity, inputs)
    }

    // "Save record" on a match. The drawer stays open on the same tab so the rest can be reviewed;
    // resolves false on failure so the button can be pressed again.
    const handleDecide = (card, decision) => saveHealthcareSearchRecord({
        applicationId: applicantId,
        payload: { card, notes: null, decision, targetKey: drawerEntity?.key || null },
    })()
        .then(() => {
            Toast({ type: 'success', message: `Record saved: ${card.displayName || 'match'}` })
            setSavedTick(k => k + 1)
            return true
        })
        .catch(() => {
            Toast({ type: 'error', message: "Couldn't save the record" })
            return false
        })

    // "Mark reviewed": none of the open matches is this entity. Each is stored as not a match, with the reason.
    const handleMarkReviewed = (cards, reason) => {
        Promise.all(cards.map(card => saveHealthcareSearchRecord({
            applicationId: applicantId,
            payload: { card, notes: reason, decision: 'DISMISSED', targetKey: drawerEntity?.key || null },
        })()))
            .catch(() => Toast({ type: 'error', message: 'Some matches could not be marked reviewed' }))
            .finally(() => setSavedTick(k => k + 1))
    }

    const handleUndo = (records) => {
        Promise.all([].concat(records).map(record => (
            deleteHealthcareSearchSavedRecord({ applicationId: applicantId, recordId: record.recordId })()
        )))
            .catch(() => Toast({ type: 'error', message: 'Failed to undo the decision' }))
            .finally(() => setSavedTick(k => k + 1))
    }

    const targetPayload = (entity, inputs) => ({
        targetKey: entity.key,
        firstName: inputs.firstName, lastName: inputs.lastName, businessName: inputs.name,
        npi: inputs.npi, state: inputs.state, dob: inputs.dob,
    })

    // Someone already on the list with the same name (any case or spacing), unless NPI or date of birth
    // tell them apart; with no name, the same NPI.
    const duplicateOf = (entity, inputs) => {
        const label = entity.kind === 'business' ? inputs.name : [inputs.firstName, inputs.lastName].filter(Boolean).join(' ')
        const npi = (inputs.npi || '').trim()
        const dob = inputs.dob || ''
        return entities.find(e => {
            if (e.key === entity.key || e.kind !== entity.kind) return false
            if (!normLabel(label)) return !!npi && e.npi === npi
            const differs = (npi && e.npi && e.npi !== npi) || (dob && e.dob && e.dob !== dob)
            return normLabel(e.name) === normLabel(label) && !differs
        }) || null
    }
    const blockDuplicate = (entity, inputs) => {
        const dup = entity.added ? duplicateOf(entity, inputs) : null
        if (dup) Toast({ type: 'error', message: `${dup.name} is already on the list` })
        return !!dup
    }

    // "Add to list" on a new entity (closes the drawer) or "Save changes" on an added one (stays open).
    // Either way nothing is searched.
    const handleSaveTarget = (entity, inputs) => {
        const label = entity.kind === 'business' ? inputs.name.trim() : [inputs.firstName.trim(), inputs.lastName.trim()].filter(Boolean).join(' ')
        if (!label && !inputs.npi.trim()) {
            Toast({ type: 'error', message: 'Enter a name or NPI' })
            return
        }
        if (blockDuplicate(entity, inputs)) return
        addHealthcareSearchTarget({ applicationId: applicantId, payload: targetPayload(entity, inputs) })()
            .then(() => loadTargets())
            .then(() => {
                if (entity.isDraft) {
                    Toast({ type: 'success', message: `${label || 'Entry'} added. Search it whenever you're ready.` })
                    closeDrawer()
                } else {
                    Toast({ type: 'success', message: 'Changes saved' })
                }
            })
            .catch(() => Toast({ type: 'error', message: entity.isDraft ? "Couldn't add to the list" : "Couldn't save the changes" }))
    }

    // Re-adding under the same key brings back the row with its past searches and saved records.
    const handleRestore = ({ target: t, kind, name, req }) => {
        const hasDetails = t.businessName || t.firstName || t.lastName || t.npi
        const [last = '', first = ''] = kind === 'person' && req.name ? req.name.split(',').map(x => x.trim()) : []
        const payload = hasDetails
            ? { targetKey: t.targetKey, firstName: t.firstName, lastName: t.lastName, businessName: t.businessName,
                npi: t.npi, state: t.state, dob: t.dob }
            : { targetKey: t.targetKey, firstName: first, lastName: last,
                businessName: kind === 'business' ? (req.name || '') : '',
                npi: req.npi || '', state: req.state || '', dob: requestDobToInput(req.dob) }
        addHealthcareSearchTarget({ applicationId: applicantId, payload })()
            .then(() => loadTargets())
            .then(() => Toast({ type: 'success', message: `${name} restored` }))
            .catch(() => Toast({ type: 'error', message: `Couldn't restore ${name}` }))
    }

    const handleRemove = (entity) => {
        removeHealthcareSearchTarget({ applicationId: applicantId, targetKey: entity.key })()
            .then(() => loadTargets())
            .then(() => {
                Toast({ type: 'success', message: `${entity.name} removed from the list` })
                closeDrawer()
            })
            .catch(() => Toast({ type: 'error', message: "Couldn't remove from the list" }))
    }

    // Opens the drawer on a new added entity; it becomes a row once added to the list or searched.
    const openAdd = () => {
        const d = { id: newId(), kind: 'person' }
        setDraft(d)
        setDrawerKey(addedKey(d))
    }

    const closeDrawer = () => {
        setDrawerKey(null)
        setDraft(null)
    }

    // Escape closes the drawer even when focus has left it (e.g. after a confirmation closed). Listens
    // last (window, bubbling): an open dialog handles Escape first and stops it, closing only itself.
    useEffect(() => {
        if (!drawerKey) return undefined
        const onKey = (ev) => {
            if (ev.key === 'Escape' && !document.querySelector('[role="dialog"]')) closeDrawer()
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [drawerKey]) // eslint-disable-line react-hooks/exhaustive-deps

    const exactCount = entities.filter(e => screenOf(e.key).ofacExact).length
    const searchedCount = entities.length - notSearched.length
    const bulkCost = showCost ? bulkList.length * cost : null
    const credits = (n) => `${n} ${n === 1 ? 'credit' : 'credits'}`
    const peopleWord = (n) => (n === 1 ? 'person or business' : 'people and businesses')

    return (
        <div className={styles.root}>
            <div className={styles.card}>
                <div className={styles.cardHead}>
                    <div className={styles.cardTitle}>
                        <h2 className={styles.title}>Healthcare Search</h2>
                        <span className={styles.summary}>
                            {entities.length} {peopleWord(entities.length)}
                            {' · '}
                            {/* "0 exact matches" before anyone is searched would read as all clear. */}
                            {searchedCount ? (
                                <span className={exactCount ? styles.summaryExact : undefined}>
                                    {exactCount} OFAC exact {exactCount === 1 ? 'match' : 'matches'}
                                </span>
                            ) : 'Not searched yet'}
                            {' · '}
                            <button type='button' className={styles.coverageLink} onClick={() => setShowCoverage(v => !v)}>
                                <Icon name='info' size={13} /> Lists checked
                            </button>
                        </span>
                    </div>
                    <div className={styles.actions}>
                        <Button variant='secondary' size='small' startIcon={<Icon name='plus' size={14} />}
                                disabled={busy} onClick={openAdd}>
                            Add person or business
                        </Button>
                        <Button variant='primary' size='small' startIcon={<Icon name='search' size={14} />}
                                disabled={!entities.length || busy} onClick={openBulk}>
                            Bulk search
                        </Button>
                    </div>
                </div>

                {showCoverage && <CoverageFooter medicaid={latestMedicaidCoverage(history)} />}

                {batch && (
                    <div className={styles.banner}>
                        <div>
                            <div className={styles.bannerTitle}>Searching {Math.min(batch.done + 1, batch.total)} of {batch.total}</div>
                            <div className={styles.bannerText}>
                                Now: {batch.current}. Each row fills in when its search finishes.
                            </div>
                        </div>
                        <Button variant='secondary' size='small' onClick={() => { stopRef.current = true }}>
                            Stop after current search
                        </Button>
                    </div>
                )}

                <div className={cx(styles.gridRow, styles.gridHead)}>
                    <span>Person or business</span>
                    <span>OFAC</span>
                    <span>Other lists</span>
                    <span>Status</span>
                </div>
                {sorted.map(e => (
                    <BoardRow
                        key={e.key}
                        entity={e}
                        entry={latestOf(e.key)}
                        s={screenOf(e.key)}
                        status={rowStatus(e.key)}
                        active={drawerKey === e.key}
                        onOpen={(list) => { setDraft(null); setDrawerTab(list || null); setDrawerKey(e.key) }}
                        onOpenRecords={screenOf(e.key).recordCount ? () => setRecordsKey(e.key) : null}
                        onRerun={busy ? null : () => requestRun(e, inputsFromEntity(e, latestOf(e.key)?.request))}
                    />
                ))}
                {removedEntities.length > 0 && (
                    <div className={styles.removedBlock}>
                        <button type='button' className={styles.removedToggle} onClick={() => setShowRemoved(v => !v)}>
                            <Icon name={showRemoved ? 'chevron-down' : 'chevron-right'} size={14} />
                            Removed ({removedEntities.length})
                        </button>
                        {showRemoved && removedEntities.map(r => (
                            <div key={r.target.targetKey} className={styles.removedRow}>
                                <div className={styles.entityCell}>
                                    <span className={styles.entityName}>{r.name}</span>
                                    <AddedTag kind={r.kind} fromApplication={r.fromApplication} />
                                </div>
                                <span className={styles.entityMeta}>
                                    {r.searches
                                        ? `${r.searches} past ${r.searches === 1 ? 'search' : 'searches'} come back with it`
                                        : 'Never searched'}
                                </span>
                                <Button variant='secondary' size='small' startIcon={<Icon name='refresh-cw' size={14} />}
                                        disabled={busy} onClick={() => handleRestore(r)}>
                                    Restore
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <SavedRecordsDialog
                entity={entities.find(e => e.key === recordsKey)}
                records={recordsKey ? decisionsFor(recordsKey) : []}
                onClose={() => setRecordsKey(null)}
                onUndo={handleUndo}
            />

            {/* Confirmations open on top of the drawer, so it must not hold focus. */}
            <Drawer anchor='right' open={!!drawerEntity} onClose={closeDrawer} ModalProps={{ disableEnforceFocus: true }}>
                {drawerEntity && (
                    <EntityDrawer
                        key={drawerEntity.key}
                        initialTab={drawerTab}
                        entity={drawerEntity}
                        draft={draft}
                        onDraftKind={(kind) => {
                            const d = { ...draft, kind }
                            setDraft(d)
                            setDrawerKey(addedKey(d))
                        }}
                        entries={historyByKey.get(drawerEntity.key) || []}
                        freshResult={freshResults[drawerEntity.key]}
                        status={rowStatus(drawerEntity.key)}
                        searching={running[drawerEntity.key] === 'searching'}
                        busy={busy}
                        applicantId={applicantId}
                        isDemoMode={isDemoMode}
                        decisions={decisionsFor(drawerEntity.key)}
                        showCost={showCost}
                        cost={cost}
                        onRun={(inputs) => requestRun(drawerEntity, inputs)}
                        onSaveTarget={(inputs) => handleSaveTarget(drawerEntity, inputs)}
                        onRemove={() => setRemoving(drawerEntity)}
                        onSave={(card) => handleDecide(card, 'CONFIRMED')}
                        onClear={handleMarkReviewed}
                        onUndo={handleUndo}
                        onClose={closeDrawer}
                    />
                )}
            </Drawer>

            <Modal
                open={!!bulkPicked}
                onClose={() => setBulkPicked(null)}
                title='Bulk search'
                footer={(
                    <>
                        <Button variant='secondary' size='small' onClick={() => setBulkPicked(null)}>Cancel</Button>
                        <Button variant='primary' size='small' disabled={!bulkList.length}
                                onClick={() => { const list = bulkList; setBulkPicked(null); runBatch(list) }}>
                            {bulkList.length
                                ? `Search ${bulkList.length}${bulkCost != null ? ` · ${credits(bulkCost)}` : ''}`
                                : 'Search'}
                        </Button>
                    </>
                )}
            >
                <p>
                    Pick who to search. Each one is checked against OFAC, LEIE, SAM, State Medicaid and NPPES
                    {showCost ? ` and uses ${credits(cost)}` : ''}.
                </p>
                <div className={styles.bulkList}>
                    <div className={styles.bulkTools}>
                        <button type='button' className={styles.linkBtn} onClick={() => setBulkPicked(new Set(entities.map(e => e.key)))}>Select all</button>
                        <button type='button' className={styles.linkBtn} onClick={() => setBulkPicked(new Set(notSearched.map(e => e.key)))}>Only not searched</button>
                        <button type='button' className={styles.linkBtn} onClick={() => setBulkPicked(new Set())}>Clear</button>
                    </div>
                    {entities.map(e => {
                        const last = latestOf(e.key)
                        return (
                            <label key={e.key} className={styles.bulkRow}>
                                <Checkbox checked={!!bulkPicked?.has(e.key)} onChange={() => toggleBulk(e.key)} />
                                <span className={styles.bulkName}>
                                    <span>{e.name}</span>
                                    <span className={styles.hint}>
                                        {roleOf(e)} · {last ? `searched ${fmtDay(last.searchedAt)}` : 'not searched yet'}
                                    </span>
                                </span>
                            </label>
                        )
                    })}
                </div>
            </Modal>

            <Modal
                open={!!removing}
                onClose={() => setRemoving(null)}
                title={removing ? `Remove ${removing.name} from this application?` : ''}
                footer={(
                    <>
                        <Button variant='secondary' size='small' onClick={() => setRemoving(null)}>Cancel</Button>
                        <Button variant='primary' size='small' startIcon={<Icon name='trash' size={14} />} onClick={() => {
                            const e = removing
                            setRemoving(null)
                            handleRemove(e)
                        }}>
                            Remove
                        </Button>
                    </>
                )}
            >
                {removing && (
                    <p>
                        {removing.name} will no longer show in this application's Healthcare Search for anyone.
                        Past searches are kept, and you can bring it back from Removed, under the table.
                    </p>
                )}
            </Modal>

            <Modal
                open={!!confirm}
                onClose={() => setConfirm(null)}
                title={confirm ? `Re-run search for ${confirm.entity.name}?` : ''}
                footer={(
                    <>
                        <Button variant='secondary' size='small' onClick={() => setConfirm(null)}>Cancel</Button>
                        <Button variant='primary' size='small' onClick={() => {
                            const c = confirm
                            setConfirm(null)
                            runSearch(c.entity, c.inputs)
                        }}>
                            Re-run{showCost ? ` · ${cost} ${cost === 1 ? 'credit' : 'credits'}` : ''}
                        </Button>
                    </>
                )}
            >
                {confirm && (
                    <p>
                        {showCost ? `This uses ${cost} ${cost === 1 ? 'credit' : 'credits'}. ` : ''}
                        The result from {fmtDateTime(latestOf(confirm.entity.key)?.searchedAt)} moves to previous
                        searches, where you can reopen it for free.
                    </p>
                )}
            </Modal>
        </div>
    )
}

// Matches the DS input (40px, control border, focus ring) so it sits in line with NPI and Date of birth.
const stateSelectStyles = {
    control: (base, s) => ({
        ...base,
        minHeight: 40,
        height: 40,
        borderRadius: 'var(--radius-md)',
        borderColor: s.isFocused ? 'var(--focus-ring)' : 'var(--border-control)',
        boxShadow: s.isFocused ? '0 0 0 1px var(--focus-ring)' : 'none',
        backgroundColor: s.isDisabled ? 'var(--bg-sunken)' : 'var(--bg-surface)',
        fontSize: 'var(--t-body)',
        '&:hover': { borderColor: s.isFocused ? 'var(--focus-ring)' : 'var(--border-control)' },
    }),
    placeholder: (base, s) => ({ ...base, color: s.isDisabled ? 'var(--fg-tertiary)' : 'var(--fg-secondary)' }),
    singleValue: (base) => ({ ...base, color: 'var(--fg-default)' }),
    indicatorSeparator: () => ({ display: 'none' }),
    menu: (base) => ({ ...base, zIndex: 20, backgroundColor: 'var(--bg-surface)' }),
    menuList: (base) => ({ ...base, maxHeight: 240 }),
    option: (base, s) => ({
        ...base,
        fontSize: 'var(--t-body)',
        color: 'var(--fg-default)',
        backgroundColor: s.isSelected ? 'var(--bg-sunken)' : s.isFocused ? 'var(--bg-hover, var(--bg-sunken))' : 'transparent',
        fontWeight: s.isSelected ? 'var(--t-weight-medium)' : undefined,
    }),
}

// Searchable state by full name. "Any state" sits in the label row so the box lines up with its
// neighbours; unticking it opens the list.
const StatePicker = ({ value, onChange }) => {
    const fieldId = useFieldId()
    const ref = useRef(null)
    const [anyState, setAnyState] = useState(!value)
    const selected = statesOptions.USA.find(o => o.value === value) || null
    return (
        <div className={fieldStyles.field}>
            <div className={styles.stateHead}>
                <label className={fieldStyles.label} htmlFor={fieldId}>State</label>
                <Checkbox
                    className={styles.anyState}
                    label='Any state'
                    checked={anyState}
                    onChange={(ev) => {
                        const on = ev.target.checked
                        setAnyState(on)
                        if (on) onChange('')
                        else setTimeout(() => ref.current?.focus(), 0)
                    }}
                />
            </div>
            <ReactSelect
                ref={ref}
                inputId={fieldId}
                options={statesOptions.USA}
                value={anyState ? null : selected}
                onChange={(o) => onChange(o?.value || '')}
                isDisabled={anyState}
                openMenuOnFocus
                isSearchable
                placeholder={anyState ? 'All states' : 'Type a state'}
                noOptionsMessage={() => 'No matching state'}
                styles={stateSelectStyles}
            />
        </div>
    )
}

// Marks people and businesses added by hand or listed as providers on the client-portal
// application, as opposed to the business and owners on the application.
const AddedTag = ({ kind, fromApplication }) => fromApplication ? (
    <span className={styles.addedTag} title='Listed as a healthcare provider on the client portal application'>
        {kind === 'business' ? 'Provider organization' : 'Individual provider'}
    </span>
) : (
    <span className={styles.addedTag} title='Added by hand for Healthcare Search; not on the application'>
        <Icon name='plus' size={11} /> {kind === 'business' ? 'Added business' : 'Added person'}
    </span>
)

// Who they are on this deal, in plain words.
const roleOf = (entity) => {
    if (!entity.added) return entity.type
    if (entity.fromApplication) return entity.kind === 'business' ? 'Provider organization from portal' : 'Provider from portal'
    return entity.kind === 'business' ? 'Added business' : 'Added person'
}

const byLine = (record) => [record?.decidedBy, fmtDay(record?.decidedAt || record?.createdAt)].filter(Boolean).join(', ')
// Name for duplicate checks: no accents, case, punctuation or extra spaces ("José Núñez" = "jose nunez").
const normLabel = (x) => (x || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`
const listNames = (keys) => keys.map(k => LISTS.find(l => l.key === k).label).join(', ').replace(/, ([^,]*)$/, ' and $1')
const openLists = (s) => EXCLUSION_LISTS.filter(k => s.lists[k].open.length)

// The short line under a row's status badge.
const statusLine = (entry, s, status) => {
    switch (status) {
        case 'SEARCHING': return 'Checking all five lists'
        case 'QUEUED': return 'Waiting for its turn'
        case 'NOT_SEARCHED': return 'Open to search'
        case 'LOADING': return null
        case 'SANCTIONED':
        case 'EXCLUDED':
        case 'PRIOR': {
            const left = openLists(s)
            const rest = left.length ? ` · ${s.openCards.length} to review on ${listNames(left)}` : ''
            return `${s.savedReason}${s.savedRecord ? ` · ${byLine(s.savedRecord)}` : ''}${rest}`
        }
        case 'EXACT': {
            // The exact matches are part of what's to review; only name the similar ones on top.
            const similar = s.openCards.length - s.openExact
            return plural(s.openExact, 'exact match', 'exact matches') + (similar > 0 ? ` · ${similar} similar` : '')
        }
        case 'TO_REVIEW': return `${s.openCards.length} to review`
        case 'ERROR': return `${s.errors.map(l => l.label).join(', ')} did not respond`
        default:
            if (s.clearRecord) return `Checked by ${byLine(s.clearRecord)}`
            return `${isStale(entry) ? 'Stale · ' : ''}Searched ${fmtDay(entry?.searchedAt)}`
    }
}

const StatusBadge = ({ status }) => (
    <Badge tone={STATUS[status].tone} title={STATUS[status].hint}
           className={STATUS[status].outline ? styles.badgeOutline : undefined}>
        {STATUS[status].label}
    </Badge>
)

const OfacCell = ({ entry, s, status, onRerun, onOpenList }) => {
    if (status === 'SEARCHING' || status === 'QUEUED') return <span className={styles.muted}>Searching…</span>
    if (!entry) return <span className={styles.muted}>—</span>
    const ofac = s.lists.OFAC
    if (ofac.status === 'ERROR') {
        return (
            <span className={styles.ofacCell}>
                <span>Didn't respond</span>
                {onRerun && (
                    <button type='button' className={styles.inlineAction}
                            onClick={(ev) => { ev.stopPropagation(); onRerun() }}>
                        Search again
                    </button>
                )}
            </span>
        )
    }
    if (!ofac.status || ofac.status === 'DISABLED') return <span className={styles.muted}>Not checked</span>
    const sub = ofac.saved.length ? <span className={styles.toneRed}>Record saved</span>
        : ofac.open.length ? <span className={styles.toneAmber}>{ofac.open.length} similar to review</span>
        : ofac.checked ? <span className={styles.muted}>{ofac.cards.length} similar, ruled out</span>
        : null
    // The cell opens the drawer on the OFAC tab.
    return (
        <Tooltip label={listTip(LISTS[0], ofac)} className={styles.tipWrap}>
            <button type='button' className={styles.cellLink} onClick={(ev) => { ev.stopPropagation(); onOpenList('OFAC') }}>
                {ofac.exact.length ? <span className={styles.ofacExact}>Exact match</span> : (
                    <span className={styles.ofacCell}>
                        <span>No exact match</span>
                        {sub}
                    </span>
                )}
            </button>
        </Tooltip>
    )
}

// Hover text for a list's cell or box, in plain words: what was found and what's left to do.
// Medicaid also says which states have no list.
const listTip = (l, info) => {
    const name = l.name
    if (info.status === 'ERROR') return `${name} didn't respond. Click to search again from the drawer.`
    if (info.status === 'DISABLED') return `${name} isn't turned on for your account.`
    let text
    if (l.key === 'NPPES') {
        text = info.count
            ? `${plural(info.count, 'provider directory record', 'provider directory records')}. NPPES is a directory, not an exclusion list.`
            : 'No provider directory record.'
    } else if (!info.count) text = `Nothing found on ${name}.`
    else if (info.saved.length) text = `Record saved on ${name}.`
    else if (info.openExact.length) text = `${plural(info.openExact.length, 'exact match', 'exact matches')} on ${name} to check.`
    else if (info.open.length) text = `${plural(info.open.length, 'similar name', 'similar names')} on ${name} to check.`
    else text = `${plural(info.count, 'match', 'matches')} on ${name}, all checked.`
    const missing = l.key === 'STATE_MEDICAID' ? info.source?.notCoveredStates || [] : []
    if (missing.length) text += ` No list in ${missing.length} ${missing.length === 1 ? 'state' : 'states'}.`
    return `${text} Click to open.`
}

// LEIE, SAM, Medicaid and NPPES side by side, 0s included so it's clear each was checked. Each box
// opens the drawer on its list.
const OtherListsCell = ({ entry, s, status, onOpenList }) => {
    if (!entry || status === 'SEARCHING' || status === 'QUEUED') return <span />
    return (
        <span className={styles.matchGrid}>
            {LISTS.filter(l => l.key !== 'OFAC').map(l => {
                const info = s.lists[l.key]
                // Same box, same place on every row: red = exact or saved, amber = similar names to check,
                // grey = other matches (NPPES directory, settled ones), dashed = 0.
                const tone = info.status === 'ERROR' ? styles.matchError
                    : info.status !== 'OK' || !info.count ? styles.matchOff
                    // NPPES is a directory, never a problem: always grey.
                    : l.key === 'NPPES' ? styles.matchHits
                    : info.openExact.length || info.saved.length ? styles.matchExact
                    : info.open.length ? styles.matchOpen
                    : styles.matchHits
                return (
                    <Tooltip key={l.key} label={listTip(l, info)} className={styles.tipWrap}>
                        <button type='button' className={cx(styles.matchCell, tone)}
                                aria-label={`${l.name}: ${info.status === 'ERROR' ? 'error' : info.count}`}
                                onClick={(ev) => { ev.stopPropagation(); onOpenList(l.key) }}>
                            <span className={styles.matchLabel}>{l.label}</span>
                            <span className={styles.matchValue}>
                                {info.status === 'ERROR' ? 'error' : info.status === 'DISABLED' ? 'off' : info.count}
                            </span>
                        </button>
                    </Tooltip>
                )
            })}
        </span>
    )
}

const BoardRow = ({ entity, entry, s, status, active, onOpen, onOpenRecords, onRerun }) => {
    const sub = statusLine(entry, s, status)
    return (
        // The whole row opens the drawer; it's focusable and opens on Enter or Space for keyboard users.
        <div className={cx(styles.gridRow, styles.row, active && styles.rowActive)} onClick={() => onOpen()}
             role='button' tabIndex={0} aria-label={`Open Healthcare Search for ${entity.name}`}
             onKeyDown={(ev) => {
                 if (ev.target === ev.currentTarget && (ev.key === 'Enter' || ev.key === ' ')) {
                     ev.preventDefault()
                     onOpen()
                 }
             }}>
            <div className={styles.entityCell}>
                <span className={styles.entityName}>{entity.name}</span>
                <span className={styles.entityType}>{roleOf(entity)}</span>
                {entity.appChanged && <span className={styles.appChanged}>Name or NPI changed on the application since last search</span>}
            </div>
            <OfacCell entry={entry} s={s} status={status} onRerun={onRerun} onOpenList={onOpen} />
            <OtherListsCell entry={entry} s={s} status={status} onOpenList={onOpen} />
            <div className={styles.statusCell}>
                <StatusBadge status={status} />
                {sub && <span className={styles.entityMeta} title={sub}>{sub}</span>}
                {onOpenRecords && (
                    <Button variant='secondary' size='small' startIcon={<Icon name='file' size={14} />}
                            className={styles.viewRecord}
                            onClick={(ev) => { ev.stopPropagation(); onOpenRecords() }}>
                        View record
                    </Button>
                )}
            </div>
        </div>
    )
}

// What to do next, in one plain sentence.
const nextStep = (entity, s, status) => {
    const name = entity.name
    switch (status) {
        case 'SANCTIONED':
        case 'EXCLUDED':
        case 'PRIOR': {
            // The badge already says it's done; only leftovers on other lists need a sentence.
            const left = openLists(s)
            return left.length ? `${plural(s.openCards.length, 'similar name', 'similar names')} on ${listNames(left)} still to check.` : null
        }
        case 'EXACT': {
            const keys = EXCLUSION_LISTS.filter(k => s.lists[k].openExact.length)
            return `Check the ${plural(s.openExact, 'exact', 'exact')} ${listNames(keys)} ${s.openExact === 1 ? 'match' : 'matches'}. Save the record if it's them, or mark ${name} as clear.`
        }
        case 'TO_REVIEW': {
            const keys = openLists(s)
            return `${name} has ${plural(s.openCards.length, 'similar name', 'similar names')} on ${plural(keys.length, 'list', 'lists')}. Open the tabs to check them, or mark ${name} as clear.`
        }
        case 'ERROR': return `${s.errors.map(l => l.name).join(' and ')} didn't respond. Search again to finish checking ${name}.`
        case 'CLEAR': return s.clearRecord ? null : 'Nothing found on any sanctions or exclusion list.'
        default: return null
    }
}

const EntityDrawer = ({
    entity, initialTab, draft, onDraftKind, entries, freshResult, status, searching, busy, applicantId, isDemoMode,
    decisions, showCost, cost, onRun, onSaveTarget, onRemove, onSave, onClear, onUndo, onClose,
}) => {
    const latest = entries[0] || null
    const [inputs, setInputs] = useState(() => inputsFromEntity(entity, latest?.request))
    const [shown, setShown] = useState({ searchId: null, response: null, loading: false })
    // Only the newest request may update the view; older ones finishing late are ignored.
    const requestToken = useRef(0)

    // Open on the latest search: the fresh response when it belongs to that search, otherwise its
    // stored result (free). Re-runs when the latest search changes, e.g. once history loads.
    useEffect(() => {
        if (freshResult && freshResult.searchId === latest?.searchId) {
            requestToken.current += 1
            setShown({ searchId: latest.searchId, response: freshResult.response, loading: false })
            return
        }
        if (!latest) return
        openEntry(latest)
    }, [freshResult, latest?.searchId]) // eslint-disable-line react-hooks/exhaustive-deps

    function openEntry(entry) {
        const token = ++requestToken.current
        setShown({ searchId: entry.searchId, response: null, loading: true })
        getHealthcareSearchResult({ applicationId: applicantId, searchId: entry.searchId, isDemoMode })()
            .then(res => {
                if (token === requestToken.current) setShown({ searchId: entry.searchId, response: res, loading: false })
            })
            .catch(() => {
                if (token !== requestToken.current) return
                Toast({ type: 'error', message: "Couldn't open that search" })
                setShown(s => ({ ...s, loading: false }))
            })
    }

    const set = (k) => (ev) => {
        const v = ev?.target?.type === 'checkbox' ? ev.target.checked : ev?.target?.value
        setInputs(f => ({ ...f, [k]: v }))
    }

    const isBusiness = entity.kind === 'business'
    // Owners and the business keep their NPI on the application; edits here only change this search.
    const npiHint = entity.added
        ? (!inputs.npi ? 'Optional. Pins the search to one provider.' : null)
        : !entity.npi
            ? `Optional. Save it on the ${entity.type === 'Owner' ? 'owner' : 'business'} to reuse it.`
            : inputs.npi !== entity.npi ? 'Changed for this search only.' : null
    // Added entries only: the form differs from the details saved on the row.
    const stored = inputsFromEntity(entity, null)
    const detailsChanged = ['firstName', 'lastName', 'name', 'npi', 'state', 'dob']
        .some(k => (inputs[k] || '').trim() !== (stored[k] || '').trim())
    // Never searched: the form is the whole drawer. Searched: the result leads, the form folds away.
    const [editing, setEditing] = useState(!latest)
    useEffect(() => { if (latest) setEditing(false) }, [latest?.searchId]) // eslint-disable-line react-hooks/exhaustive-deps
    const [detailsOpen, setDetailsOpen] = useState(false)
    // Opened from an OFAC cell or list box, the drawer starts on that list; reopening another search
    // goes back to the default tab.
    const [tab, setTab] = useState(initialTab || null)
    const lastSearchId = useRef(null)
    useEffect(() => {
        if (lastSearchId.current && lastSearchId.current !== shown.searchId) setTab(null)
        lastSearchId.current = shown.searchId
    }, [shown.searchId])
    const [clearing, setClearing] = useState(false)
    const [note, setNote] = useState('')
    const [undoingClear, setUndoingClear] = useState(false)

    const shownEntry = entries.find(e => e.searchId === shown.searchId) || latest
    const runLabel = latest ? 'Re-run' : 'Run search'
    const runButton = (
        <Button variant='primary' size='small' loading={searching} disabled={busy}
                onClick={() => onRun(inputs)}
                endIcon={showCost ? <CreditCost cost={cost} /> : undefined}>
            {runLabel}
        </Button>
    )

    const s = screen(shownEntry, shown.response, decisions)
    const ready = !!latest && !!shown.response && !searching && !shown.loading
    // Badge, sentence and actions describe the search on screen, which can be an older one.
    const shownStatus = ready ? s.status : status
    const sentence = ready ? nextStep(entity, s, shownStatus) : null
    const canClear = ready && s.clearable.length > 0
    // With a saved record they stay Sanctioned or Excluded; clearing only checks off the leftovers.
    const clearingLeftovers = s.savedCount > 0
    const clearLabel = clearingLeftovers
        ? `Mark the ${plural(s.clearable.length, 'similar name', 'similar names')} as clear`
        : `Mark ${entity.name} as clear`
    const req = shownEntry?.request || {}
    const searchedName = readableName(req.targetLabel || req.name)
    const npiShown = entity.npi || req.npi

    // "Mark … as clear": every open match on every list is ruled out, with the note.
    const clearSummary = () => {
        const parts = EXCLUSION_LISTS
            .map(k => [k, s.clearable.filter(c => s.lists[k].open.includes(c)).length])
            .filter(([, n]) => n)
            .map(([k, n]) => `${n} ${LISTS.find(l => l.key === k).label}`)
        const stays = clearingLeftovers ? ` ${entity.name} stays ${STATUS[shownStatus]?.label || 'as is'}.` : ''
        return `${parts.join(', ')} ${s.clearable.length === 1 ? 'match is' : 'matches are'} not ${entity.name}.${stays}`
    }
    // The latest "Mark … as clear", with its note and Undo, shown whatever the status: a list that
    // didn't respond (Error) or new matches from a re-run must not hide it.
    const lastClear = s.clearRecords.slice().sort((a, b) => (b.decidedAt || '').localeCompare(a.decidedAt || ''))[0] || null
    const clearableExact = s.clearable.filter(c => EXCLUSION_LISTS.some(k => s.lists[k].openExact.includes(c))).length
    // Pin the tab the drawer opened on, so saving or clearing (which changes the default) doesn't
    // switch lists under the reviewer.
    useEffect(() => { if (ready && !tab) setTab(defaultList(s)) }, [ready, tab]) // eslint-disable-line react-hooks/exhaustive-deps
    const clearLead = s.savedCount > 0 ? 'Similar names marked clear'
        : shownStatus === 'CLEAR' ? 'Marked clear' : 'Some matches marked clear'

    return (
        <div className={styles.drawer}>
            <div className={styles.drawerHead}>
                <div>
                    <h3 className={styles.drawerTitle}>{entity.isDraft ? 'Add person or business' : entity.name}</h3>
                    {!entity.isDraft && (
                        <div className={styles.hint}>
                            {roleOf(entity)}{npiShown ? ` · NPI ${npiShown}` : ''}
                        </div>
                    )}
                </div>
                <IconButton label='Close' onClick={onClose}><Icon name='x' size={18} /></IconButton>
            </div>

            {!entity.isDraft && latest && (
                <div className={styles.drawerStatus}>
                    <div className={styles.statusRow}>
                        <StatusBadge status={shownStatus} />
                    </div>
                    {searching && <div className={styles.sentence}>Searching all five lists…</div>}
                    {!searching && shown.loading && <div className={styles.hint}>Loading the result…</div>}
                    {sentence && <div className={styles.sentence}>{sentence}</div>}
                    {ready && lastClear && (
                        <div className={shownStatus === 'CLEAR' ? styles.sentence : styles.hint}>
                            {clearLead} by {byLine(lastClear)}
                            {lastClear.notes ? <span title={lastClear.notes}> · {lastClear.notes}</span> : null}
                            {' · '}
                            <button type='button' className={styles.linkBtn} onClick={() => setUndoingClear(true)}>Undo</button>
                        </div>
                    )}
                    {entity.appChanged && (
                        <div className={styles.appChanged}>Name or NPI changed on the application since last search. Search again to check the current details.</div>
                    )}
                    {canClear && (
                        <div>
                            <Button size='small' className={styles.clearBtn} onClick={() => { setNote(''); setClearing(true) }}>
                                {clearLabel}
                            </Button>
                        </div>
                    )}
                </div>
            )}

            {ready && (
                <MatchTabs
                    key={shown.searchId}
                    screening={s}
                    tab={tab || defaultList(s)}
                    onTab={setTab}
                    decisionOf={(card) => decisionFor(decisions, card)}
                    searchedName={searchedName}
                    searchedNpi={req.npi}
                    entityName={entity.name}
                    onSave={onSave}
                    onUndo={onUndo}
                    onRerun={busy ? null : () => onRun(inputs)}
                />
            )}

            {latest && (
                <div className={styles.searchLine}>
                    <button type='button' className={styles.searchLineText} onClick={() => setDetailsOpen(v => !v)}>
                        <Icon name={detailsOpen ? 'chevron-down' : 'chevron-right'} size={14} />
                        <span>
                            Searched as '{searchedName}' · {req.npi ? `NPI ${req.npi}` : 'No NPI'} · {req.state || 'Any state'}
                            {' · '}{fmtDay(shownEntry?.searchedAt)}{shownEntry?.searchedBy ? ` by ${shownEntry.searchedBy}` : ''}
                            {shownEntry !== latest ? ' (older search)' : ''}
                        </span>
                    </button>
                    <span className={styles.summaryActions}>
                        <button type='button' className={styles.linkBtn} onClick={() => setEditing(v => !v)}>
                            {editing ? 'Hide search' : 'Edit search'}
                        </button>
                        {!editing && runButton}
                    </span>
                </div>
            )}

            {latest && detailsOpen && (
                <div className={styles.searchDetails}>
                    <div className={styles.detailsGrid}>
                        <div><div className={styles.detailLabel}>Name</div><div>{searchedName || '—'}</div></div>
                        <div><div className={styles.detailLabel}>NPI</div><div>{req.npi || 'None'}</div></div>
                        <div><div className={styles.detailLabel}>State</div><div>{req.state || 'Any state'}</div></div>
                        <div><div className={styles.detailLabel}>Searched</div><div>{fmtDateTime(shownEntry?.searchedAt)}{shownEntry?.searchedBy ? ` by ${shownEntry.searchedBy}` : ''}</div></div>
                    </div>
                    <div>
                        <div className={styles.detailLabel}>Previous searches</div>
                        {entries.length > 1 ? (
                            <div className={styles.history}>
                                {entries.map((entry, i) => (
                                    <button key={entry.searchId} type='button'
                                            className={cx(styles.historyItem, shown.searchId === entry.searchId && styles.historyItemActive)}
                                            onClick={() => openEntry(entry)}>
                                        <div>
                                            <div><strong>{fmtDateTime(entry.searchedAt)}</strong>{i === 0 ? ' · Latest' : ''}</div>
                                            <div className={styles.hint}>
                                                {describeOutcome(entry)}{entry.searchedBy ? ` · ${entry.searchedBy}` : ''}
                                            </div>
                                        </div>
                                        <span className={styles.hint}>{shown.searchId === entry.searchId ? 'Showing' : 'Reopen · free'}</span>
                                    </button>
                                ))}
                            </div>
                        ) : <div className={styles.hint}>This is the first search.</div>}
                    </div>
                </div>
            )}

            {editing && (
                <div className={styles.section}>
                    {entity.isDraft && (
                        <ChoiceGroup row legend='Type'>
                            <Radio name='added-kind' label='Person' checked={draft?.kind !== 'business'}
                                   onChange={() => onDraftKind('person')} />
                            <Radio name='added-kind' label='Business' checked={draft?.kind === 'business'}
                                   onChange={() => onDraftKind('business')} />
                        </ChoiceGroup>
                    )}
                    {/* Business: name on its own line, then NPI + State. Person: names, then NPI + State + DOB. */}
                    <div className={isBusiness ? styles.formGrid1 : styles.formGrid}>
                        {isBusiness ? (
                            <Input label='Business name' value={inputs.name} onChange={set('name')} />
                        ) : (
                            <>
                                <Input label='First name' value={inputs.firstName} onChange={set('firstName')} />
                                <Input label='Last name' value={inputs.lastName} onChange={set('lastName')} />
                            </>
                        )}
                    </div>
                    <div className={isBusiness ? styles.formGrid : styles.formGrid3}>
                        <Input label='NPI' value={inputs.npi} maxLength={10} onChange={set('npi')} hint={npiHint} />
                        <StatePicker value={inputs.state} onChange={(v) => setInputs(f => ({ ...f, state: v }))} />
                        {!isBusiness && (
                            <Input label='Date of birth' type='date' value={inputs.dob} onChange={set('dob')} />
                        )}
                    </div>
                    <div className={styles.formRow}>
                        <Switch label='Include reinstated / prior sanctions' checked={inputs.includeInactive}
                                onChange={set('includeInactive')} />
                        {entity.added ? (
                            <div className={styles.summaryActions}>
                                <Button variant='secondary' size='small'
                                        disabled={busy || searching || (!entity.isDraft && !detailsChanged)}
                                        onClick={() => onSaveTarget(inputs)}>
                                    {entity.isDraft ? 'Add to list' : 'Save changes'}
                                </Button>
                                {runButton}
                            </div>
                        ) : runButton}
                    </div>
                </div>
            )}

            {entity.added && !entity.isDraft && (
                <div className={styles.removeRow}>
                    <Button variant='secondary' size='small' startIcon={<Icon name='trash' size={14} />}
                            disabled={busy || searching || s.savedCount > 0} onClick={onRemove}>
                        Remove from this application
                    </Button>
                    {s.savedCount > 0 && <span className={styles.hint}>Can't remove while a record is saved. Undo the saved record first.</span>}
                </div>
            )}

            <Modal
                open={clearing}
                onClose={() => setClearing(false)}
                title={`${clearLabel}?`}
                footer={(
                    <>
                        <Button variant='secondary' size='small' onClick={() => setClearing(false)}>Cancel</Button>
                        <Button size='small' className={styles.clearBtn} disabled={!note.trim()}
                                onClick={() => { setClearing(false); onClear(s.clearable, note.trim()) }}>
                            Mark as clear
                        </Button>
                    </>
                )}
            >
                <p>
                    {clearSummary()}
                    {clearableExact > 0 && (
                        <strong className={styles.toneRed}> This includes {plural(clearableExact, 'exact match', 'exact matches')}.</strong>
                    )}
                </p>
                <Input label='Note' multiline rows={3} value={note} maxLength={2000}
                       placeholder={`Why is ${entity.name} clear?`} onChange={(ev) => setNote(ev.target.value)} />
            </Modal>
            <Modal
                open={undoingClear}
                onClose={() => setUndoingClear(false)}
                title={`Undo "clear" for ${entity.name}?`}
                footer={(
                    <>
                        <Button variant='secondary' size='small' onClick={() => setUndoingClear(false)}>Cancel</Button>
                        <Button variant='primary' size='small' onClick={() => { setUndoingClear(false); onUndo(s.clearRecords) }}>Undo</Button>
                    </>
                )}
            >
                <p>
                    {s.clearRecords.length === 1 ? 'The match goes' : `The ${s.clearRecords.length} matches go`} back
                    to "to review".
                </p>
            </Modal>
        </div>
    )
}

const VERDICT_TONE = { EXCLUDED: 'declined', PRIOR_SANCTIONS: 'watch' }

// One line per confirmed match; the full Healthcare Check opens on demand.
const ConfirmedRecord = ({ record, defaultOpen, onUndo }) => {
    const [open, setOpen] = useState(defaultOpen)
    const [undoing, setUndoing] = useState(false)
    const card = snapshotOf(record)
    const verdict = card ? deriveVerdict(card, record.decision) : null
    const tone = record.decision === 'DISMISSED' ? 'approved' : VERDICT_TONE[card?.status] || 'approved'
    return (
        <div className={styles.recordItem}>
            <button type='button' className={styles.recordSummary} onClick={() => setOpen(v => !v)}>
                <Icon name={open ? 'chevron-down' : 'chevron-right'} size={16} />
                <span className={styles.entityName}>{record.matchedName}</span>
                {verdict && <Badge tone={tone}>{verdict.text}</Badge>}
                <span className={styles.hint}>
                    {[record.npi && `NPI ${record.npi}`, record.providerType, record.state].filter(Boolean).join(' · ')}
                </span>
                <span className={cx(styles.hint, styles.recordDate)}>{fmtDateTime(record.createdAt)}</span>
            </button>
            {open && (
                <div className={styles.recordBody}>
                    {card
                        ? <HealthcareCheckDetail card={card} decision={record.decision} />
                        : <div className={styles.hint}>The saved snapshot could not be read.</div>}
                    {/* Undo lives here too: a saved match can drop out of later searches, leaving the
                        drawer nothing to undo it from. */}
                    {onUndo && (
                        <div>
                            <Button variant='secondary' size='small' onClick={() => setUndoing(true)}>Undo saved record</Button>
                        </div>
                    )}
                </div>
            )}
            <Modal
                open={undoing}
                onClose={() => setUndoing(false)}
                title='Undo the saved record?'
                footer={(
                    <>
                        <Button variant='secondary' size='small' onClick={() => setUndoing(false)}>Cancel</Button>
                        <Button variant='primary' size='small' onClick={() => { setUndoing(false); onUndo([record]) }}>Undo</Button>
                    </>
                )}
            >
                <p>{record.matchedName} will no longer be saved as a record.</p>
            </Modal>
        </div>
    )
}

// Full audit view of what was decided for one entity: each confirmed match as the Healthcare Check
// record, built from the snapshot taken at decision time; ruled-out matches listed below.
const SavedRecordsDialog = ({ entity, records, onClose, onUndo }) => {
    const confirmed = records.filter(r => r.decision !== 'DISMISSED')
    const dismissed = records.filter(r => r.decision === 'DISMISSED')
    return (
        <Dialog open={!!entity && records.length > 0} onClose={onClose} maxWidth='md' fullWidth>
            {entity && (
                <div className={styles.recordsDialog}>
                    <div className={styles.drawerHead}>
                        <div>
                            <h3 className={styles.drawerTitle}>Saved records</h3>
                            <div className={styles.hint}>{entity.name} · {roleOf(entity)}</div>
                        </div>
                        <IconButton label='Close' onClick={onClose}><Icon name='x' size={18} /></IconButton>
                    </div>

                    {confirmed.length > 0 && (
                        <div className={styles.section}>
                            <div className={styles.sectionTitle}>Saved ({confirmed.length})</div>
                            {confirmed.map(r => (
                                <ConfirmedRecord key={r.recordId} record={r} defaultOpen={confirmed.length === 1} onUndo={onUndo} />
                            ))}
                        </div>
                    )}

                    {dismissed.length > 0 && (
                        <div className={styles.section}>
                            <div className={styles.sectionTitle}>Marked clear ({dismissed.length})</div>
                            {/* Names cleared together share one note; it shows once, above the first of them. */}
                            {dismissed.map((r, i) => (
                                <div key={r.recordId} className={styles.dismissedRow}>
                                    <span>{r.matchedName}</span>
                                    <span className={styles.hint}>
                                        {[r.npi && `NPI ${r.npi}`, r.providerType, r.state].filter(Boolean).join(' · ')}
                                    </span>
                                    <span className={styles.hint}>
                                        {fmtDateTime(r.decidedAt || r.createdAt)}{r.decidedBy ? ` · ${r.decidedBy}` : ''}
                                    </span>
                                    {r.notes && r.notes !== dismissed[i - 1]?.notes && (
                                        <span className={cx(styles.hint, styles.dismissedNote)}>Note: {r.notes}</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </Dialog>
    )
}

// What each list is, in the same order as everywhere else.
const COVERAGE = [
    ['OFAC', 'U.S. Treasury sanctions list, matched on names and known aliases.'],
    ['LEIE', 'Federal health care exclusions (HHS OIG), including reinstated records when prior sanctions are included.'],
    ['SAM', 'Government-wide exclusions (SAM.gov).'],
    ['State Medicaid', "Every state with a list on file is checked, whatever state you search. Each result names the states with no list; nothing found there isn't a clean result."],
    ['NPPES', 'National provider directory. Confirms identity, NPI, taxonomy and license. Not an exclusion list.'],
]

// Medicaid coverage from the newest search on this application (history is newest first).
const latestMedicaidCoverage = (history) => {
    for (const entry of history) {
        const src = (entry.sources || []).find(x => x.source === 'STATE_MEDICAID' && x.status === 'OK')
        if (src && (src.coveredStates || src.notCoveredStates)) return src
    }
    return null
}

const CoverageFooter = ({ medicaid }) => (
    <div className={styles.footer}>
        <strong>Lists checked</strong>
        <div className={styles.hint}>
            Every search checks all five lists. Nothing found means no record on that list, within what the list covers.
        </div>
        <div className={styles.footerGrid}>
            {COVERAGE.map(([name, text]) => (
                <div key={name}>
                    <div className={styles.footerSource}>{name}</div>
                    <div className={styles.hint}>{text}</div>
                    {name === 'State Medicaid' && medicaid && (
                        <div className={styles.coverageStates}>
                            {(medicaid.coveredStates || []).length} states checked.
                            {(medicaid.notCoveredStates || []).length > 0 && (
                                <> No list for: {medicaid.notCoveredStates.map(stateName).join(', ')}.</>
                            )}
                        </div>
                    )}
                </div>
            ))}
        </div>
    </div>
)

export default EntityBoard
